import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Sparkles, ShieldCheck, AlertTriangle, Plus } from "lucide-react";
import AppShell, { getActiveSchemeId } from "@/components/AppShell";
import { useAuth } from "@/auth/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const STATUS_STYLE = {
  active:         { bg: "var(--success-light)", color: "var(--success)", label: "Active" },
  renewing_soon:  { bg: "var(--warning-light)", color: "var(--warning)", label: "Renewing soon" },
  expired:        { bg: "var(--alert-light)",   color: "var(--alert)",   label: "EXPIRED" },
};

const BAND_STYLE = {
  likely_adequate:    { color: "var(--success)", label: "Likely adequate" },
  likely_under:       { color: "var(--alert)",   label: "Likely under-insured" },
  likely_over:        { color: "var(--blue-dark)", label: "Likely over-insured" },
  insufficient_data:  { color: "var(--warning)", label: "Insufficient data" },
};

const $ = (n) => n == null ? "—" : `$${Number(n).toLocaleString()}`;
const daysUntil = (iso) => Math.round((new Date(iso) - Date.now()) / 86400000);

export default function Insurance() {
  const { user } = useAuth();
  const schemeId = getActiveSchemeId();
  const [policies, setPolicies] = useState([]);
  const [filter, setFilter] = useState(null);
  const [me, setMe] = useState(null);
  const [quoteCount, setQuoteCount] = useState(null);
  const [activePolicy, setActivePolicy] = useState(null);
  const [adequacy, setAdequacy] = useState(null);
  const [adequacyBusy, setAdequacyBusy] = useState(false);
  const [adequacyRateLimit, setAdequacyRateLimit] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);

  const reload = async () => {
    const [p, q, members] = await Promise.all([
      api.get(`/schemes/${schemeId}/insurance`),
      api.get(`/schemes/${schemeId}/insurance-quotes`),
      api.get(`/schemes/${schemeId}/members`),
    ]);
    setPolicies(p.data);
    setQuoteCount(q.data.count);
    const mine = members.data.find((m) => m.user_id === user?.user_id);
    setMe(mine || (user?.system_role === "platform_admin" ? { role: "chair" } : null));
  };

  useEffect(() => { reload().catch((e) => toast.error(apiError(e))); /* eslint-disable-next-line */ }, []);

  const isChairOrCommittee = me?.role === "chair" || me?.role === "committee";
  const filtered = filter ? policies.filter((p) => p.status === filter) : policies;

  const runAdequacy = async (policy_id) => {
    setAdequacy(null);
    setAdequacyRateLimit(null);
    setAdequacyBusy(true);
    try {
      const { data } = await api.post(`/ai/insurance/check-adequacy`, { scheme_id: schemeId, policy_id });
      setAdequacy(data);
      reload();
    } catch (e) {
      if (e?.response?.status === 429) {
        const secs = parseInt(e.response.headers["retry-after"] || "3600", 10);
        const mins = Math.max(1, Math.ceil(secs / 60));
        setAdequacyRateLimit(`AI request limit reached. Try again in about ${mins} minute${mins === 1 ? "" : "s"}.`);
      } else {
        toast.error(apiError(e));
      }
    } finally { setAdequacyBusy(false); }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8 lg:py-10" data-testid="insurance-page">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] mb-2" style={{ color: "var(--blue-dark)" }}>
              <Shield size={12}/> Insurance Command
            </span>
            <h1 className="text-2xl font-bold" style={{ color: "var(--navy)" }}>Insurance policies</h1>
            <p className="text-sm" style={{ color: "var(--slate)" }}>
              Track renewal dates, run AI adequacy checks, and request broker quotes.
            </p>
          </div>
          {isChairOrCommittee && (
            <div className="flex items-center gap-2">
              <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="add-policy-btn"><Plus size={12} className="mr-1.5"/> Add policy</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <PolicyForm onSubmit={async (form) => {
                    try { await api.post(`/schemes/${schemeId}/insurance`, form); toast.success("Policy added"); setAddOpen(false); reload(); }
                    catch (e) { toast.error(apiError(e)); }
                  }} onCancel={() => setAddOpen(false)}/>
                </DialogContent>
              </Dialog>
              <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" style={{ background: "var(--blue)", color: "white" }} data-testid="request-quote-btn"><Sparkles size={12} className="mr-1.5"/> Request broker quote</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <QuoteForm defaults={{ contact_name: user?.name || "", contact_email: user?.email || "" }}
                    onSubmit={async (form) => {
                      try { await api.post(`/schemes/${schemeId}/insurance-quotes`, form); toast.success("Submitted — broker will be notified once email integration is live (Phase 6)"); setQuoteOpen(false); reload(); }
                      catch (e) { toast.error(apiError(e)); }
                    }} onCancel={() => setQuoteOpen(false)}/>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5 mb-4 flex-wrap" data-testid="insurance-filter">
          {[{k:null,l:"All"},{k:"renewing_soon",l:"Renewing soon"},{k:"active",l:"Active"},{k:"expired",l:"Expired"}].map((f) => (
            <button key={f.k || "all"} onClick={() => setFilter(f.k)}
              data-testid={`insurance-filter-${f.k || "all"}`}
              className="px-3 py-1.5 text-xs font-medium rounded-full border"
              style={{
                borderColor: filter === f.k ? "var(--blue)" : "var(--border)",
                background: filter === f.k ? "var(--blue-light)" : "white",
                color: filter === f.k ? "var(--blue-dark)" : "var(--slate)",
              }}>{f.l}</button>
          ))}
          {isChairOrCommittee && quoteCount > 0 && (
            <span className="ml-auto text-[11px]" style={{ color: "var(--slate)" }} data-testid="quote-count">
              {quoteCount} quote request{quoteCount === 1 ? "" : "s"} submitted
            </span>
          )}
        </div>

        {filtered.length === 0 ? (
          <div className="rounded-xl border bg-white px-6 py-12 text-center" style={{ borderColor: "var(--border)" }} data-testid="insurance-empty">
            <Shield size={28} className="mx-auto mb-3" style={{ color: "var(--tagline-grey)" }}/>
            <p className="text-sm" style={{ color: "var(--slate)" }}>No policies match.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="insurance-list">
            {filtered.map((p) => {
              const st = STATUS_STYLE[p.status];
              const days = daysUntil(p.period_end);
              const counterColor = days < 0 ? "var(--alert)" : days < 30 ? "var(--alert)" : days < 60 ? "var(--warning)" : "var(--success)";
              return (
                <div key={p.policy_id} className="rounded-xl border bg-white p-4" style={{ borderColor: "var(--border)" }} data-testid={`policy-card-${p.policy_id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--tagline-grey)" }}>{p.policy_type.replace(/_/g, " ")}</div>
                      <div className="font-semibold text-sm truncate" style={{ color: "var(--navy)" }}>{p.insurer_name}</div>
                      <div className="text-[11px]" style={{ color: "var(--slate)" }}>Policy {p.policy_number}</div>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: st.bg, color: st.color }} data-testid={`policy-status-${p.policy_id}`}>{st.label}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t" style={{ borderColor: "var(--border)" }}>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--slate)" }}>Sum insured</div>
                      <div className="font-bold tabular-nums text-sm" style={{ color: "var(--navy)" }}>{$(p.sum_insured)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--slate)" }}>Renews</div>
                      <div className="font-bold tabular-nums text-sm" style={{ color: counterColor }} data-testid={`policy-countdown-${p.policy_id}`}>
                        {days < 0 ? `${-days}d ago` : `in ${days}d`}
                      </div>
                    </div>
                  </div>
                  {p.latest_adequacy_check && (
                    <div className="mt-3 pt-3 border-t text-[11px]" style={{ borderColor: "var(--border)", color: "var(--slate)" }} data-testid={`policy-latest-adequacy-${p.policy_id}`}>
                      Adequacy: <strong style={{ color: BAND_STYLE[p.latest_adequacy_check.band]?.color }}>{BAND_STYLE[p.latest_adequacy_check.band]?.label || p.latest_adequacy_check.band}</strong> ({p.latest_adequacy_check.score}/100) · last run {new Date(p.latest_adequacy_check.ran_at).toLocaleDateString()}
                    </div>
                  )}
                  {p.linked_compliance_item_id && (
                    <Link to={`/scheme/compliance/${p.linked_compliance_item_id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded mt-3 hover:underline" style={{ background: "var(--blue-light)", color: "var(--blue-dark)" }} data-testid={`policy-compliance-link-${p.policy_id}`}>
                      <ShieldCheck size={9}/> Linked compliance item
                    </Link>
                  )}
                  {isChairOrCommittee && (
                    <Button size="sm" variant="outline" className="mt-3 w-full" asChild data-testid={`policy-open-${p.policy_id}`}>
                      <Link to={`/scheme/insurance/${p.policy_id}`}>Open policy details</Link>
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {adequacyRateLimit && (
          <div className="rounded-2xl border p-4 mt-6 flex items-start gap-3" style={{ borderColor: "var(--warning)", background: "var(--warning-light)" }} data-testid="adequacy-rate-limit">
            <AlertTriangle size={16} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 2 }}/>
            <p className="text-xs" style={{ color: "var(--navy)" }}>{adequacyRateLimit}</p>
          </div>
        )}

        {adequacy && (
          <div className="rounded-2xl border bg-white p-5 mt-6" style={{ borderColor: "var(--border)" }} data-testid="adequacy-result">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: "var(--blue-light)", color: BAND_STYLE[adequacy.adequacy_band]?.color }} data-testid="adequacy-band">
                {BAND_STYLE[adequacy.adequacy_band]?.label || adequacy.adequacy_band}
              </span>
              <span className="text-2xl font-bold tabular-nums" style={{ color: BAND_STYLE[adequacy.adequacy_band]?.color }} data-testid="adequacy-score">{adequacy.score}/100</span>
              <span className="text-[10px]" style={{ color: "var(--tagline-grey)" }}>{adequacy.model}</span>
            </div>
            <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm" style={{ color: "var(--navy)" }} data-testid="adequacy-reasoning">{adequacy.reasoning}</div>
            {adequacy.suggested_actions?.length > 0 && (
              <div className="mt-4">
                <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--slate)" }}>Suggested actions</div>
                <ul className="space-y-1 text-sm" data-testid="adequacy-actions">
                  {adequacy.suggested_actions.map((a, i) => <li key={i} className="flex gap-2"><span style={{ color: "var(--blue)" }}>•</span><span style={{ color: "var(--navy)" }}>{a}</span></li>)}
                </ul>
              </div>
            )}
            <p className="text-[11px] mt-4 pt-3 border-t italic" style={{ borderColor: "var(--border)", color: "var(--tagline-grey)" }}>{adequacy.disclaimer}</p>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function PolicyForm({ onSubmit, onCancel }) {
  const [f, setF] = useState({
    policy_type: "building", insurer_name: "", policy_number: "",
    sum_insured: 0, premium: 0, period_start: "", period_end: "",
    broker_name: "", broker_contact_email: "", notes: "",
  });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  return (
    <>
      <DialogHeader><DialogTitle>Add insurance policy</DialogTitle></DialogHeader>
      <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...f, sum_insured: Number(f.sum_insured), premium: Number(f.premium) }); }} className="space-y-3 mt-2" data-testid="policy-form">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Policy type</Label>
          <Select value={f.policy_type} onValueChange={set("policy_type")}>
            <SelectTrigger className="mt-1" data-testid="policy-type-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              {["building","common_contents","voluntary_workers","office_bearers_liability","public_liability","lift","machinery_breakdown","loss_of_rent","other"].map((t) =>
                <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <FF label="Insurer" value={f.insurer_name} onChange={set("insurer_name")} testid="policy-insurer" required/>
          <FF label="Policy number" value={f.policy_number} onChange={set("policy_number")} testid="policy-number" required/>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <FF label="Sum insured ($)" type="number" value={f.sum_insured} onChange={set("sum_insured")} testid="policy-sum" required/>
          <FF label="Premium ($)" type="number" value={f.premium} onChange={set("premium")} testid="policy-premium"/>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <FF label="Period start" type="date" value={f.period_start} onChange={set("period_start")} testid="policy-pstart" required/>
          <FF label="Period end" type="date" value={f.period_end} onChange={set("period_end")} testid="policy-pend" required/>
        </div>
        <FF label="Broker contact email" value={f.broker_contact_email} onChange={set("broker_contact_email")} testid="policy-broker-email"/>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" style={{ background: "var(--blue)", color: "white" }} data-testid="policy-submit">Add policy</Button>
        </DialogFooter>
      </form>
    </>
  );
}

function QuoteForm({ defaults, onSubmit, onCancel }) {
  const [f, setF] = useState({
    broker_target: "BurMac",
    policy_types: ["building"],
    current_renewal_date: "",
    contact_name: defaults.contact_name || "",
    contact_phone: "",
    contact_email: defaults.contact_email || "",
    notes: "",
  });
  const [acknowledged, setAcknowledged] = useState(false);
  const [disclosure, setDisclosure] = useState("");
  useEffect(() => {
    api.get("/legal/afsl-disclosure").then((r) => setDisclosure(r.data.disclosure || "")).catch(() => {});
  }, []);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  return (
    <>
      <DialogHeader><DialogTitle>Request broker quote</DialogTitle></DialogHeader>
      <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...f, acknowledge_commission_disclosure: true }); }} className="space-y-3 mt-2" data-testid="quote-form">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Broker</Label>
          <Select value={f.broker_target} onValueChange={set("broker_target")}>
            <SelectTrigger className="mt-1" data-testid="quote-broker-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="BurMac">BurMac Strata Brokers</SelectItem>
              <SelectItem value="other">Other broker</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <FF label="Current renewal date" type="date" value={f.current_renewal_date} onChange={set("current_renewal_date")} testid="quote-renewal"/>
        <div className="grid grid-cols-2 gap-2">
          <FF label="Contact name" value={f.contact_name} onChange={set("contact_name")} testid="quote-contact-name" required/>
          <FF label="Contact phone" value={f.contact_phone} onChange={set("contact_phone")} testid="quote-contact-phone" required/>
        </div>
        <FF label="Contact email" value={f.contact_email} onChange={set("contact_email")} testid="quote-contact-email" required/>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Notes</Label>
          <Textarea className="mt-1" rows={3} value={f.notes} onChange={set("notes")} data-testid="quote-notes"/>
        </div>
        <div className="rounded border bg-amber-50 p-3" style={{ borderColor: "#fde68a" }} data-testid="quote-afsl-callout">
          <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: "#92400e" }}>AFSL commission disclosure</div>
          <div className="text-xs leading-relaxed max-h-32 overflow-y-auto" style={{ color: "#78350f" }}>{disclosure || "Loading disclosure…"}</div>
          <label className="flex items-center gap-2 mt-3 text-xs cursor-pointer" style={{ color: "#78350f" }}>
            <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} data-testid="quote-ack-checkbox"/>
            I have read Opstrait's commission and referral fee disclosure
          </label>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={!acknowledged} style={{ background: acknowledged ? "var(--blue)" : "var(--slate-light)", color: acknowledged ? "white" : "var(--slate)" }} data-testid="quote-submit">Submit request</Button>
        </DialogFooter>
      </form>
    </>
  );
}

function FF({ label, testid, ...rest }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>{label}</Label>
      <Input className="mt-1" data-testid={testid} {...rest}/>
    </div>
  );
}
